import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Actions, concatLatestFrom, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { combineLatest, of, Subject, timer } from 'rxjs';
import { catchError, concatMap, filter, first, map, mapTo, mergeMapTo, pluck, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';

import * as ParcelShopActions from './actions';
import * as ParcelShopSelectors from './selectors';
import * as ParcelShopReducers from './reducers';

import { environment } from '@app-env/environment';
import { Action, select, Store } from '@ngrx/store';
import { ApiResponse } from '@app/core/models';
import { ParcelShopFromAPI } from '@app/models';
import { transformParcelShop } from '@app/models/parcel-shop.utils';

const CLOCK_RESOLUTION = 5 * 60 * 1000;

@Injectable()
export class ParcelShopEffects implements OnInitEffects {
	private _appIsStable$ = new Subject<boolean>();

	load$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ParcelShopActions.load),
			concatLatestFrom(action => this.store.pipe(select(ParcelShopSelectors.selectIsLoadedOrLoadring))),
			// tap(([action, isLoadedOrLoading]) => {
			// 	console.log('parcelshops isLoadedOrLoading?', isLoadedOrLoading);
			// }),
			filter(([action, isLoadedOrLoading]) => !isLoadedOrLoading),
			mapTo(ParcelShopActions.loadFromApi())
		);
	});

	loadFromApi$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ParcelShopActions.loadFromApi),
			// tap(( action ) => {
			// 	console.log('parcelshops effect load from api', isLoadedOrLoading);
			// }),
			switchMapTo(
				this.httpClient.get<ApiResponse<ParcelShopFromAPI[]>>(environment.API_URL + 'parcel-shops').pipe(
					pluck('data'),
					map(response => response.map(transformParcelShop)),
					map(response => ParcelShopActions.loadSuccess({ parcelShops: response })),
					catchError((error: HttpErrorResponse) => {
						Sentry.withScope(scope => {
							scope.setTag('module', 'site');
							scope.setExtra('error', error);
							Sentry.captureMessage('API error');
						});
						return of(ParcelShopActions.loadFailed());
					})
				)
			)
		);
	});

	init$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ParcelShopActions.init),
			mergeMapTo([
				// ParcelShopActions.load(),
				ParcelShopActions.startClock(),
			])
		);
	});

	startClock$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ParcelShopActions.startClock),
			switchMapTo(this._appIsStable$.asObservable()),
			switchMap(() => {
				// console.log('App is stable, start updateClock timer');
				const diff = CLOCK_RESOLUTION - (new Date().getTime() % CLOCK_RESOLUTION) + 1000;
				return timer(diff, CLOCK_RESOLUTION).pipe(switchMapTo(of(ParcelShopActions.updateClock())));
			})
		);
	});

	constructor(
		private actions$: Actions,
		private store: Store<ParcelShopReducers.State>,

		private httpClient: HttpClient,
		private _appRef: ApplicationRef,
		private _ngZone: NgZone
	) {
		this._appRef.isStable.pipe(first(stable => stable)).subscribe(() => {
			console.log('App is stable.');
			this._ngZone.run(() => {
				this._appIsStable$.next(true);
			});
		});
	}

	ngrxOnInitEffects(): Action {
		return ParcelShopActions.init();
	}
}
